import { useMemo } from 'react';
import { ContentHeaderSingleProps } from '@croquiscom-pvt/zds';
import { useRouter } from 'next/router';

import { useUBL } from '@common/log-manager';
import { useInitLikedProducts } from '@widgets/like-product';
import {
  convertProductItemListToUxGoodsCardItemList,
  convertProductListToIdList,
  convertToWebUrlFromDeeplink,
  ProductGroupComponentType,
  useCarouselMoreButtonSlideWidth,
  useEnvConfig,
} from '@domains/storehome-common';

import { getObjectSection, ProductGroupContentsType } from '../utils/getObjectSection';

interface Params {
  shop_id: string;
  category_id?: string;
  component: ProductGroupComponentType;
}

export const useProductGroupService = ({ shop_id, category_id, component }: Params) => {
  const router = useRouter();
  const { query } = router;
  const tracker = useUBL();
  const { is_zigzag } = useEnvConfig();

  const slide_width = useCarouselMoreButtonSlideWidth();
  const is_related_products = component.contents_type === ProductGroupContentsType.RELATED_PRODUCTS;
  const carousel_padding_bottom = is_related_products ? 16 : 20;
  const content_header_size = (is_related_products ? 'small' : 'medium') as ContentHeaderSingleProps['size'];

  const {
    product_carousel: { component_list, more_button: product_carousel_more_button },
    more_button,
    contents_type,
  } = component;

  const product_card_list = useMemo(
    () =>
      convertProductItemListToUxGoodsCardItemList({
        item_list: component_list,
        is_zigzag,
        show_ranking: true,
      }),
    [component_list, is_zigzag],
  );

  useInitLikedProducts({
    data: product_card_list,
    convert: convertProductListToIdList,
  });

  const handleMoreButtonClick = () => {
    if (!more_button || !more_button.link_url) {
      return;
    }

    if (contents_type === ProductGroupContentsType.BEST_PRODUCTS) {
      tracker({
        category: 'click',
        navigation: category_id ? 'shop_category_detail' : 'shop_detail',
        navigation_sub: { shop_id },
        object_section: 'best',
        object_type: 'button',
        object_id: 'see_more',
      });
    } else {
      tracker({
        category: 'click',
        navigation: 'shop_detail',
        navigation_sub: { shop_id },
        object_section: getObjectSection(contents_type) ?? '',
        object_type: 'button',
        object_id: 'see_more',
      });
    }

    const link = convertToWebUrlFromDeeplink(more_button.link_url, {
      shop_id,
      main_domain: query.main_domain as string,
    });
    router.push(link);
  };

  const handleCarouselMoreButtonClick = () => {
    if (!product_carousel_more_button || !product_carousel_more_button.link_url) {
      return;
    }
    const link = convertToWebUrlFromDeeplink(product_carousel_more_button.link_url, {
      shop_id,
      main_domain: query.main_domain as string,
    });
    tracker({
      category: 'click',
      navigation: 'shop_detail',
      navigation_sub: { shop_id },
      object_section: getObjectSection(contents_type) ?? '',
      object_type: 'button',
      object_id: 'carousel_more',
    });
    router.push(link);
  };

  return {
    slide_width,
    product_card_list,
    carousel_padding_bottom,
    content_header_size,
    handleCarouselMoreButtonClick,
    handleMoreButtonClick,
  };
};
